// dropdown-options-location.service.ts
import { Injectable } from '@angular/core';
import { DropdownOptionsService } from 'src/app/core/services/dropdown-options.service';
import { ILocationDataService } from 'src/app/shared/service/ilocation-data-service.service';
import { LocationOption } from '../components/model/LocationOption';
import { isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { AddressMaintainanceService } from 'src/app/features/setting/service/address-maintainance.service';

/**
 * Service that adapts your existing DropdownOptionsService to work with the new LocationCascadeDropdownComponent
 * This service acts as a bridge between your existing API service and the new component interface
 */
@Injectable({
  providedIn: 'root'
})
export class DropdownOptionsLocationService implements ILocationDataService {

  constructor(
    private dropdownOptionsService: DropdownOptionsService,
    private addressMaintainanceService: AddressMaintainanceService
  ) { }
  getRegions(): Promise<LocationOption[]> {
    throw new Error('Method not implemented.');
  }

  /**
   * Get countries using your existing DropdownOptionsService
   */
  async getCountries(): Promise<LocationOption[]> {
    return new Promise((resolve, reject) => {
      this.dropdownOptionsService.getVCountryOptions().subscribe({
        next: (options: any[]) => {
          const locationOptions = options.map(x => ({
            label: x.countryName,
            value: x.countryCode,
            countryCode: x.countryCode,
            countryName: x.countryName
          }));

          resolve(locationOptions);
        },
        error: (error) => {
          console.error('Failed to load countries:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Get cities using your existing DropdownOptionsService
   */
  async getCities(): Promise<LocationOption[]> {
    return new Promise((resolve, reject) => {
      this.dropdownOptionsService.getVCityOptions().subscribe({
        next: (options: any[]) => {
          const locationOptions = options.map(x => ({
            label: x.name,
            value: x.seqNo.toString(),
            country: x.country,
            countryCode: x.country,
            cityName: x.name,
            cityCode: x.seqNo.toString(),
            seqNo: x.seqNo, // 保留原始數值給 district 使用
            // Store additional properties that might be useful
            expressArea: x.expressArea
          }));

          resolve(locationOptions);
        },
        error: (error) => {
          console.error('Failed to load cities:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Get areas using your existing DropdownOptionsService
   */
  async getAreas(): Promise<LocationOption[]> {
    return new Promise((resolve, reject) => {
      this.dropdownOptionsService.getVAreaOptions().subscribe({
        next: (options: any[]) => {
          const locationOptions = options.map(x => ({
            label: x.areaName,
            value: x.areaCode,
            city: x.city,
            cityCode: x.city,
            areaName: x.areaName,
            areaCode: x.areaCode
          }));

          resolve(locationOptions);
        },
        error: (error) => {
          console.error('Failed to load areas:', error);
          reject(error);
        }
      });
    });
  }


  /**
   * Get express areas - 根據您原本的邏輯實現
   * 從 city options 中提取 unique expressArea
   */
  async getExpressAreas(): Promise<LocationOption[]> {
    return new Promise((resolve, reject) => {
      this.dropdownOptionsService.getVCityOptions().subscribe({
        next: (options: any[]) => {
          const expressAreaOptions = [...new Set(options.map(x => x.expressArea))]
            .filter(x => !isNullOrEmpty(x))
            .map(x => ({
              label: x,
              value: x,
              expressArea: x
            }));

          resolve(expressAreaOptions);
        },
        error: (error) => {
          console.error('Failed to load express areas:', error);
          reject(error);
        }
      });
    });
  }

  /**
   * Get districts by country and city - 新增功能
   */
  async getDistricts(country: string, city: number): Promise<LocationOption[]> {
    try {
      const assignmentOptions = await this.addressMaintainanceService.getAssignmentDistrict(country, city);

      const locationOptions = assignmentOptions.map((x: any) => ({
        label: x.districtName,
        value: x.assignmentDistrictId,
        country: country,
        city: city,
        districtName: x.districtName,
        districtId: x.assignmentDistrictId,
        assignmentDistrictId: x.assignmentDistrictId
      }));

      return locationOptions;
    } catch (error) {
      console.error('Failed to load districts:', error);
      throw error;
    }
  }

  /**
   * Alternative method to get regions by city if you have a separate regions API
   */
  async getRegionsByCity(cityId: string): Promise<LocationOption[]> {
    // If you have a separate regions API, implement it here
    // For now, returning empty array as placeholder
    return [];
  }

  /**
   * Helper method to get express areas (original implementation)
   * This maintains compatibility with your existing logic
   */
  async getExpressAreasByCity(cityValue: any): Promise<LocationOption[]> {
    return new Promise((resolve, reject) => {
      this.dropdownOptionsService.getVCityOptions().subscribe({
        next: (options: any[]) => {
          // 如果需要根據城市過濾 expressArea
          const filteredOptions = cityValue && cityValue !== 'ALL'
            ? options.filter(x => x.seqNo.toString() === cityValue)
            : options;

          const expressAreaOptions = [...new Set(filteredOptions.map(x => x.expressArea))]
            .filter(x => !isNullOrEmpty(x))
            .map(x => ({
              label: x,
              value: x,
              expressArea: x,
              city: cityValue
            }));

          resolve(expressAreaOptions);
        },
        error: (error) => {
          console.error('Failed to load express areas by city:', error);
          reject(error);
        }
      });
    });
  }
}
